import type { SalesLeaderboardDrillDown, SalesLeaderboardRole } from '../dashboardv2.store';

/**
 * Query params that limit a report to one leaderboard row's person, in the role of the tab, or to the jobs
 * with nobody in that role for the Unassigned row.
 */
export function salesLeaderboardPersonParams(userId: string | null, role: SalesLeaderboardRole): Record<string, string> {
  if (role === 'Estimator') {
    return userId ? { jobEstimators: userId } : { noEstimator: 'true' };
  }
  return userId ? { jobSalesAgents: userId } : { noSalesAgent: 'true' };
}

/**
 * Query params for the report a Sales Leaderboard cell drills into.
 *
 * Kept as a pure function so the per-tab rules can be unit tested without the component — most
 * importantly that `#7020`'s customer no-show exclusion reaches the Estimator tab's links only.
 */
export function buildSalesLeaderboardDrillDownParams(
  dd: SalesLeaderboardDrillDown,
  userId: string | null,
  baseParams: Record<string, string>,
  role: SalesLeaderboardRole,
): Record<string, string> {
  // Requirement: ticket 7056 - some columns count differently on the Estimator tab.
  const view = role === 'Estimator' && dd.estimator ? { ...dd, ...dd.estimator } : dd;
  const params: Record<string, string> = {
    zone: baseParams['zone'],
    period: baseParams['period'] || 'Custom',
    customPeriod: baseParams['customPeriod'],
    currency: baseParams['currency'],
    periodType: view.periodType,
    groupBy: view.groupBy,
    dataColumns: view.dataColumns.join(';'),
  };
  Object.assign(params, salesLeaderboardPersonParams(userId, role));
  if (view.eventStatuses?.length) {
    params.eventStatuses = view.eventStatuses.join(';');
  }
  if (view.eventSharedStatuses?.length) {
    params.eventSharedStatuses = view.eventSharedStatuses.join(';');
  }
  if (view.jobStages?.length) {
    params.jobStage = view.jobStages.join(';');
  }
  if (role === 'Estimator' && view.estimatorExcludeClosedReasons?.length) {
    params.excludeClosedReasons = view.estimatorExcludeClosedReasons.join(';');
  }
  if (baseParams['excludeDamages']) {
    params.excludeDamages = baseParams['excludeDamages'];
  }
  if (baseParams['currencySource']) {
    params.currencySource = baseParams['currencySource'];
  }
  return params;
}
